import React, { useState, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Label, Legend } from "recharts";

import CustomTooltip from "./components/Tooltip";
import CustomLegend from "./components/Legend";
import { useConfig, useFormattedData } from "./hooks";
import type { Props } from "./types.d";

export * from "./types.d";

export default function CustomLineChart({ data, config }: Props) {
  const { xAxes, yAxes, lines } = useConfig(data, config);
  const formattedData = useFormattedData(data, xAxes, yAxes);

  const [lineState, setLineState] = useState<Record<string, boolean>>(() => lines.reduce((acc, line) => ({ ...acc, [line.dataKey]: true }), {}));

  const handleLegendClick = useCallback((data: { value: string }) => {
    setLineState(state => ({
      ...state,
      [data.value]: !state[data.value],
    }));
  }, []);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={formattedData}
        margin={{
          top: 25,
          right: 20,
          bottom: 35,
          left: 30,
        }}
      >
        {xAxes.map(({ label, dataKey, stroke, xAxis, ticks, domain }) => (
          <XAxis
            allowDataOverflow
            key={dataKey}
            dataKey={item => item[dataKey].value}
            name={dataKey}
            width={xAxis?.width}
            axisLine={false}
            tickLine={false}
            stroke={stroke}
            tickFormatter={xAxis?.tickFormatter}
            minTickGap={20}
            scale="utc"
            ticks={ticks}
            domain={domain}
            type="number"
            tickCount={xAxis?.tickCount}
            padding={{ left: 20, right: 20 }}
            orientation={xAxis?.position}
            tick={{ fontSize: "12px", fontFamily: "Lab Grotesque Mono" }}
          >
            <Label
              className="stroke-neutral-900"
              value={label}
              offset={0}
              position={xAxis?.position}
              stroke={stroke}
              dy={10}
              fontSize="16px"
              fontFamily="Lab Grotesque Mono"
              letterSpacing="0.5px"
            />
          </XAxis>
        ))}

        {yAxes.map(({ label, dataKey, stroke, yAxis, domain, ticks }) => (
          <YAxis
            key={dataKey}
            name={dataKey}
            yAxisId={dataKey}
            type="number"
            allowDataOverflow
            axisLine={false}
            tickLine={false}
            width={yAxis?.width}
            stroke={stroke}
            tickFormatter={yAxis?.tickFormatter}
            ticks={ticks}
            domain={domain}
            tickCount={yAxis?.tickCount}
            orientation={yAxis?.position}
            padding={{ top: 20, bottom: 20 }}
            tick={{ fontSize: "12px", fontFamily: "Lab Grotesque Mono", letterSpacing: "0.5px" }}
          >
            <Label
              value={label}
              offset={0}
              angle={yAxis?.position === "left" ? -90 : 90}
              dx={-10}
              position={yAxis?.position}
              stroke={stroke}
              fontSize="16px"
              fontFamily="Lab Grotesque Mono"
              letterSpacing="0.5px"
              style={{ textAnchor: "middle", dominantBaseline: "middle" }}
            />
          </YAxis>
        ))}

        <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#404040" }} wrapperStyle={{ outline: "none" }} />
        <Legend content={<CustomLegend />} align="left" wrapperStyle={{ bottom: "0px", left: "90px", paddingLeft: "3px", paddingRight: "3px" }} onClick={handleLegendClick} />

        {lines.map(({ dataKey, stroke, line }) => (
          <Line
            key={dataKey}
            name={dataKey}
            hide={!lineState[dataKey]}
            yAxisId={dataKey}
            dataKey={item => item[dataKey].value}
            stroke={stroke}
            strokeWidth={1}
            strokeDasharray={line?.strokeDasharray}
            dot={{ r: line?.dotR, strokeDasharray: "", className: "fill-neutral" }}
            activeDot={{ r: 6 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
